'use client';
import { Button, Checkbox, Column, Modal, Row, Typography } from '@viewup/ui';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';

export type PopupModalProps = {
  items: { src: string; title: string; content: string; link: string; condition: () => boolean }[];
};

const PREVIOUS_COOKIE_NAME = 'modalPopup-event';
const CURRENT_COOKIE_NAME = 'modalPopup-event-new';

export const PopupModal = ({ items }: PopupModalProps) => {
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  // 쿠키에서 값을 읽어오는 함수
  const getCookie = (name: string): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return undefined;
  };

  // 쿠키에 값을 설정하는 함수
  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    if (days === 1) {
      date.setHours(23, 59, 59, 999); // 쿠키 만료일 설정
    } else {
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000); // 쿠키 만료일 설정
    }
    document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
  };

  useEffect(() => {
    // 이전(legacy) 팝업 쿠키가 있으면 삭제
    const prevCookie = getCookie(PREVIOUS_COOKIE_NAME);
    if (prevCookie !== undefined) {
      document.cookie = `${PREVIOUS_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    const modalPopup = getCookie(CURRENT_COOKIE_NAME);
    if (modalPopup === 'true') {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
  }, []);

  const handleDismiss = () => {
    setIsOpen(false);
    setCookie(CURRENT_COOKIE_NAME, 'true', 1); // 하루 동안 쿠키 유지
  };

  const handleSkipModal = () => {
    setIsOpen(false);
    setCookie(CURRENT_COOKIE_NAME, 'true', 999); // 영원히 보지 않기
  };

  return (
    <Modal
      exitHandler={() => {
        return swiperInstance.slideNext();
      }}
      hasCloseButton={false}
      isOpen={isOpen}
      styledComponents={{
        root: {
          css: {
            minWidth: '300px',
            maxWidth: '300px',
            '& > div': {
              borderRadius: 0,
            },
            '& button': {
              color: '#ffffff',
            },
            '& img': {
              objectFit: 'cover',
            },
            borderRadius: 0,
            overflow: 'visible',
          },
          p: 0,
        },
      }}
    >
      <Column>
        <Swiper
          loop={true}
          modules={[Pagination]}
          onSlideChange={(swiper) => setCurrentPage(swiper.realIndex)}
          onSwiper={(swiper) => setSwiperInstance(swiper)}
          pagination={{
            el: paginationRef.current,
            clickable: true,
          }}
          style={{ width: '100%' }}
        >
          {items
            .filter((item) => item.condition())
            .map((item) => (
              <SwiperSlide key={item.src} style={{ maxWidth: 'max-content' }}>
                <Image
                  alt={'popup'}
                  height={240}
                  sizes='300px'
                  src={item.src}
                  unoptimized
                  width={300}
                />
                <Column gap={8} p={20}>
                  <Typography fontType='header3.bold'>{item.title}</Typography>
                  <Typography color='text.secondary' fontType='body2.regular'>
                    {item.content}
                  </Typography>
                </Column>
              </SwiperSlide>
            ))}
        </Swiper>
        <Row alignItems='center' m={20} mt={0}>
          <Button onClick={handleDismiss} size='XS' variant='ghost.assistive'>
            <Typography color='text.interactive.secondary' fontType='body2.regular'>
              건너뛰기
            </Typography>
          </Button>
          <Row
            alignItems='center'
            css={{
              '& .swiper-pagination-bullet': {
                transition: 'width 0.25s ease-in-out',
                width: 8,
                height: 8,
                margin: '0 !important',
                borderRadius: 128,
                cursor: 'pointer',
              },
              '& .swiper-pagination-bullet-active': {
                width: 16,
              },
            }}
            gap={8}
            height={20}
            justifyContent='center'
            ref={paginationRef}
            width='100%'
          />
          <Button
            onClick={() => router.push(items[currentPage].link)}
            size='XS'
            variant='ghost.assistive'
          >
            <Typography color='text.interactive.primary' fontType='body2.regular'>
              자세히보기
            </Typography>
          </Button>
        </Row>
        <Row css={{ bottom: -36, cursor: 'pointer', left: 0 }} position='absolute' width='100%'>
          <Checkbox
            checkboxSize='2XS'
            label='다시 보지 않기'
            onChange={handleSkipModal}
            styledComponents={{
              label: {
                color: 'system.white',
              },
            }}
          />
        </Row>
      </Column>
    </Modal>
  );
};
